// One error shape for every failed API call. The backend answers errors with
// problem+json ({title, status, detail, errors[], request_id}); anything else
// (a network failure, or nginx's HTML 429 page) is normalized to the same fields.
export class ApiError extends Error {
  constructor({ status = 0, title = '', detail = '', errors = [], requestId = null, retryAfter = null }) {
    super(detail || title || 'Request failed');
    this.name = 'ApiError';
    this.status = status;
    this.title = title;
    this.detail = detail;
    this.errors = errors;
    this.requestId = requestId;
    this.retryAfter = retryAfter;
  }
}

export const toApiError = (error) => {
  if (error instanceof ApiError) return error;
  const response = error && error.response;
  if (!response) {
    return new ApiError({ detail: 'Could not reach the server. Check your connection and try again.' });
  }
  const body = response.data && typeof response.data === 'object' ? response.data : {};
  const retryAfter = parseInt(response.headers && response.headers['retry-after'], 10);
  return new ApiError({
    status: response.status,
    title: body.title || '',
    detail: body.detail || '',
    errors: body.errors || [],
    requestId: body.request_id || null,
    retryAfter: Number.isNaN(retryAfter) ? null : retryAfter,
  });
};

// The text to show the user for a failed call. `fallback` describes what was
// being done ("Something went wrong while posting your comment.").
export const errorMessage = (error, fallback = 'Something went wrong. Please try again.') => {
  const err = toApiError(error);
  let message;
  if (err.status === 429) {
    message = err.retryAfter
      ? `Too many requests. Please try again in ${err.retryAfter} s.`
      : 'Too many requests. Please try again shortly.';
  } else if (err.status === 403 && err.title === 'Only the creator can change this') {
    message = 'Only the creator can change this.';
  } else if (err.status === 422 && err.errors.length > 0) {
    message = err.errors.map((e) => e.msg || e.message || String(e)).join('\n');
  } else if (err.status >= 500 || err.status === 0) {
    message = err.status === 0 ? err.detail : fallback;
  } else {
    message = err.detail || err.title || fallback;
  }
  return err.requestId && err.status >= 500 ? `${message}\n(Report this with ID ${err.requestId})` : message;
};

// Alerts are how this app reports failures everywhere, so callers can just
// `catch (err) { alertError(err, '...') }`.
export const alertError = (error, fallback) => {
  console.error(error);
  alert(errorMessage(error, fallback));
};
